import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Activity,
  ExternalLink,
  RefreshCw,
  Route as RouteIcon,
  X,
} from "lucide-react";
import {
  rankSuggestions,
  type Coordinate,
  type RouteSuggestion,
  type StravaLink,
} from "../../../shared/strava";
import {
  normalizeRoute,
  safeStravaLink,
  stravaRequest,
  type StravaStatus,
} from "../lib/strava";
import RouteMap from "./RouteMap";

function StravaModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const root = useRef<HTMLElement>(null),
    close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    root.current?.querySelector<HTMLElement>("button")?.focus();
    function key(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        close.current();
      }
      if (event.key !== "Tab") return;
      const items = Array.from(
        root.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled),a[href],input:not(:disabled),[tabindex="0"]',
        ) ?? [],
      );
      const first = items[0],
        last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      previous?.focus();
    };
  }, []);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section
        ref={root}
        className="modal strava-preview"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </section>
    </div>
  );
}

function useStatus() {
  const [status, setStatus] = useState<StravaStatus | null>(null),
    [error, setError] = useState("");
  const reload = useCallback(async () => {
    try {
      setStatus(await stravaRequest<StravaStatus>("status"));
      setError("");
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  useEffect(() => {
    if (
      !status?.connected ||
      !["pending", "syncing"].includes(status.sync_state)
    )
      return;
    const timer = window.setInterval(() => void reload(), 5000);
    return () => window.clearInterval(timer);
  }, [status?.connected, status?.sync_state, reload]);
  return { status, error, reload };
}
function ConnectButton({
  beforeConnect,
  onError,
}: {
  beforeConnect?: () => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  async function connect() {
    setBusy(true);
    try {
      beforeConnect?.();
      const result = await stravaRequest<{ url: string }>("start", {
        return_path: window.location.pathname,
      });
      window.location.assign(result.url);
    } catch (e) {
      onError((e as Error).message);
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      className="strava-connect"
      disabled={busy}
      aria-label={busy ? "Kobler til Strava" : "Connect with Strava"}
      onClick={() => void connect()}
    >
      <img
        src="/strava/connect-with-strava.svg"
        alt="Connect with Strava"
        width="237"
        height="48"
      />
    </button>
  );
}
function ImportStatus({
  status,
  retry,
}: {
  status: StravaStatus;
  retry: () => void;
}) {
  return (
    <div className="strava-import" aria-live="polite">
      {["pending", "syncing"].includes(status.sync_state) && (
        <p>
          Finner rutene dine… {status.imported_count} aktiviteter hentet. Du kan
          fortsette med planen.
        </p>
      )}
      {status.sync_state === "ready" && (
        <p>
          {status.route_count} ruteforslag fra de siste 12 månedene. Avstandene
          er omtrentlige.
        </p>
      )}
      {status.sync_state === "error" && (
        <p role="alert">{status.error || "Synkronisering feilet."}</p>
      )}
      <button
        className="text-button"
        type="button"
        onClick={retry}
        disabled={["pending", "syncing"].includes(status.sync_state)}
      >
        <RefreshCw size={14} /> Oppdater fra Strava
      </button>
    </div>
  );
}
export function StravaSettings() {
  const { status, error, reload } = useStatus(),
    [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  if (status && !status.features.enabled) return null;
  async function action(name: string) {
    setBusy(true);
    setMessage("");
    try {
      await stravaRequest(name, {});
      await reload();
      if (name === "disconnect") {
        setMessage(
          "Strava er koblet fra. Strava-data og tilknyttede ruter er slettet fra Join.",
        );
        window.dispatchEvent(new Event("join:strava-updated"));
      }
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="strava-panel" aria-labelledby="strava-settings-title">
      <h2 id="strava-settings-title">
        <Activity size={19} /> Strava
      </h2>
      {!status?.connected && (
        <>
          <p>
            Koble til for å finne vanlige ruter og vise aktivitetene dine på
            gjennomførte planer.
          </p>
          <ConnectButton onError={setMessage} />
        </>
      )}
      {status?.connected && (
        <>
          <ImportStatus status={status} retry={() => void action("retry")} />
          <p className="muted compact">
            Når du kobler fra, fjernes Strava-lenker og ruter fra planene.
            Møtepunkt og resten av planen beholdes.
          </p>
          <button
            type="button"
            className="text-button danger"
            disabled={busy}
            onClick={() => void action("disconnect")}
          >
            Koble fra Strava
          </button>
        </>
      )}
      {(message || error) && <p role="status">{message || error}</p>}
      {new URLSearchParams(window.location.search).get("strava") ===
        "error" && (
        <p role="alert">
          Tilkoblingen ble ikke fullført. Prøv igjen og gi tilgang til
          aktiviteter.
        </p>
      )}
    </section>
  );
}
export function StravaRoutePicker({
  activityId,
  meeting,
  selected,
  onSelect,
  beforeConnect,
  restoreId,
}: {
  activityId: string;
  meeting: Coordinate | null;
  selected: RouteSuggestion | null;
  onSelect: (route: RouteSuggestion | null) => void;
  beforeConnect: () => void;
  restoreId?: string | null;
}) {
  const { status, error, reload } = useStatus();
  const [routes, setRoutes] = useState<RouteSuggestion[]>([]),
    [preview, setPreview] = useState<RouteSuggestion | null>(null),
    [message, setMessage] = useState("");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!status?.connected || !status.features.routes) {
      setRoutes([]);
      return;
    }
    let disposed = false;
    stravaRequest<RouteSuggestion[]>("suggestions")
      .then((rows) => {
        if (!disposed) {
          setRoutes(rows.filter((r) => normalizeRoute(r)));
          setLoaded(true);
        }
      })
      .catch((e) => {
        if (!disposed) setMessage(e.message);
      });
    return () => {
      disposed = true;
    };
  }, [
    status?.connected,
    status?.features.routes,
    status?.last_synced_at,
    status?.sync_state,
    status?.route_count,
  ]);
  useEffect(() => {
    if (!restoreId || selected || !loaded) return;
    const route = routes.find(
      (r) => r.id === restoreId && r.activity_id === activityId,
    );
    if (route) onSelect(route);
  }, [restoreId, loaded, routes, activityId, selected, onSelect]);
  const ranked = useMemo(
    () =>
      rankSuggestions(
        routes.filter((r) => r.activity_id === activityId),
        meeting,
      ),
    [routes, activityId, meeting?.[0], meeting?.[1]],
  );
  const common = ranked.filter((r) => r.frequency >= 2).slice(0, 6),
    recent = ranked
      .filter((r) => r.frequency === 1)
      .slice(0, 6 - common.length);
  if (status && (!status.features.enabled || !status.features.routes))
    return null;
  if (!["run", "ride", "walk", "ski", "swim"].includes(activityId)) return null;
  async function retry() {
    try {
      await stravaRequest("retry", {});
      await reload();
    } catch (e) {
      setMessage((e as Error).message);
    }
  }
  function cards(items: RouteSuggestion[]) {
    return (
      <div className="strava-route-grid">
        {items.map((route) => (
          <article className="strava-route-choice" key={route.id}>
            <RouteMap route={route} label={route.name} />
            <button
              type="button"
              onClick={() => setPreview(route)}
              aria-label={`Forhåndsvis ${route.name}`}
            >
              <strong>{route.name}</strong>
              <small>
                {route.frequency >= 2 ? `${route.frequency} turer · ` : ""}Sist{" "}
                {new Date(route.last_used_at).toLocaleDateString("nb-NO")}
              </small>
            </button>
          </article>
        ))}
      </div>
    );
  }
  return (
    <section
      className="strava-panel route-picker"
      aria-labelledby="planned-route-title"
    >
      <div className="strava-section-heading">
        <h2 id="planned-route-title">
          <RouteIcon size={19} /> Planlagt rute
        </h2>
        <span className="muted">Valgfritt</span>
      </div>
      {!status?.connected && (
        <>
          <p>
            Finn en rute du kjenner fra Strava. Møtepunktet velger du separat.
          </p>
          <ConnectButton beforeConnect={beforeConnect} onError={setMessage} />
        </>
      )}
      {status?.connected && (
        <>
          <ImportStatus status={status} retry={() => void retry()} />
          {selected && (
            <div className="selected-route">
              <strong>{selected.name}</strong>
              <span>
                Ca. {(selected.distance_m / 1000).toFixed(1).replace(".", ",")}{" "}
                km
              </span>
              <button
                type="button"
                className="text-button"
                onClick={() => onSelect(null)}
              >
                Fjern rute
              </button>
              <RouteMap
                route={selected}
                meeting={meeting}
                label="Valgt rute og møtepunkt"
              />
            </div>
          )}
          {common.length > 0 && (
            <>
              <h3>Dine vanlige ruter</h3>
              {cards(common)}
            </>
          )}
          {recent.length > 0 && (
            <>
              <h3>Nylige ruter</h3>
              <p className="muted compact">
                Disse rutene har én registrert tur.
              </p>
              {cards(recent)}
            </>
          )}
          {loaded && ranked.length === 0 && status.sync_state === "ready" && (
            <p>
              Ingen tilgjengelige GPS-ruter for denne aktiviteten ennå. Du kan
              opprette planen med bare et møtepunkt.
            </p>
          )}
        </>
      )}
      {(message || error) && <p role="status">{message || error}</p>}
      {preview && (
        <StravaModal title="Forhåndsvis rute" onClose={() => setPreview(null)}>
          <div className="strava-section-heading">
            <h2>{preview.name}</h2>
            <button
              type="button"
              className="icon-only"
              aria-label="Lukk ruteforhåndsvisning"
              onClick={() => setPreview(null)}
            >
              <X size={18} />
            </button>
          </div>
          <RouteMap
            route={preview}
            meeting={meeting}
            label="Ruteforhåndsvisning med møtepunkt"
            interactive
          />
          <p>
            Ca. {(preview.distance_m / 1000).toFixed(1).replace(".", ",")} km.
            Dette er en tidligere registrert rute.
          </p>
          <p>
            Ruten deles med dem som kan se planen. Møtepunktet ditt beholdes.
          </p>
          <button
            type="button"
            className="primary-action"
            onClick={() => {
              onSelect(preview);
              setPreview(null);
            }}
          >
            Bruk denne ruten
          </button>
        </StravaModal>
      )}
    </section>
  );
}
export function StravaIndicator() {
  return (
    <span className="strava-indicator" title="Strava-aktivitet tilknyttet">
      <Activity size={14} aria-hidden="true" />
      <span>Strava</span>
    </span>
  );
}
export function PlanStravaActivity({
  planId,
  link,
  isHost,
  onChanged,
}: {
  planId: string;
  link?: StravaLink | null;
  isHost: boolean;
  onChanged: () => Promise<void>;
}) {
  const { status } = useStatus(),
    [open, setOpen] = useState(false),
    [items, setItems] = useState<
      { id: string; name: string; start_date: string }[]
    >([]),
    [page, setPage] = useState(1),
    [more, setMore] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const url = safeStravaLink(link?.activity_url),
    canManage = isHost && status?.connected && status.features.links;
  async function load(next = 1) {
    setBusy(true);
    setOpen(true);
    setError("");
    try {
      const result = await stravaRequest<{
        activities: typeof items;
        more: boolean;
      }>("activities", undefined, { plan_id: planId, page: String(next) });
      setItems((old) =>
        next === 1 ? result.activities : [...old, ...result.activities],
      );
      setMore(result.more);
      setPage(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function change(action: string, id?: string) {
    setBusy(true);
    setError("");
    try {
      await stravaRequest(action, { plan_id: planId, activity_id: id });
      await onChanged();
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (!url && !canManage) return null;
  return (
    <section className="strava-panel plan-strava">
      {url && (
        <a
          className="strava-link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <StravaIndicator /> View on Strava <ExternalLink size={15} />
        </a>
      )}
      {canManage && (
        <div className="strava-actions">
          {link?.source === "auto" && <small>Matchet automatisk</small>}
          <button
            type="button"
            className="text-button"
            disabled={busy}
            onClick={() => void load()}
          >
            {url ? "Endre aktivitet" : "Koble til Strava-aktivitet"}
          </button>
          {url && (
            <button
              type="button"
              className="text-button"
              disabled={busy}
              onClick={() => void change("dismiss")}
            >
              Fjern kobling
            </button>
          )}
        </div>
      )}
      {error && <p role="alert">{error}</p>}
      {open && (
        <StravaModal
          title="Velg Strava-aktivitet"
          onClose={() => setOpen(false)}
        >
          <div className="strava-section-heading">
            <h2>Velg aktivitet</h2>
            <button
              type="button"
              className="icon-only"
              aria-label="Lukk aktivitetsvelger"
              onClick={() => setOpen(false)}
            >
              <X size={18} />
            </button>
          </div>
          {!busy && !items.length && (
            <p>Ingen aktiviteter funnet rundt tidspunktet for planen.</p>
          )}
          {error && <p role="alert">{error}</p>}
          {items.map((item) => (
            <button
              type="button"
              className="strava-activity-choice"
              key={item.id}
              disabled={busy}
              onClick={() => void change("link", item.id)}
            >
              <strong>{item.name}</strong>
              <small>{new Date(item.start_date).toLocaleString("nb-NO")}</small>
            </button>
          ))}
          {busy && <p role="status">Henter…</p>}
          {more && (
            <button
              type="button"
              className="text-button"
              disabled={busy}
              onClick={() => void load(page + 1)}
            >
              Vis flere
            </button>
          )}
        </StravaModal>
      )}
    </section>
  );
}
